import {
  TOOLTIP_CANDIDATE_SELECTOR,
  TOOLTIP_DATETIME_ATTRIBUTE,
  TOOLTIP_DERIVED_LABEL_ATTRIBUTE,
  TOOLTIP_ICON_SELECTOR,
  TOOLTIP_NEUTRALIZED_TITLE_ATTRIBUTE,
  TOOLTIP_TEXT_ATTRIBUTE,
} from './tooltip.constants';

/** Met en forme la valeur machine d'une date avec heure, ou `null`. */
export type DateTimeTooltipFormatter = (rawDateTime: string) => string | null;

/** Élément qui déclenche l'infobulle, et le texte à y afficher. */
export interface TooltipTrigger {
  anchor: Element;
  text: string;
}

const ARIA_LABEL = 'aria-label';
const ARIA_LABELLEDBY = 'aria-labelledby';
const TITLE = 'title';

/**
 * L'élément n'affiche-t-il que des icônes ? Vrai pour une icône elle-même, ou
 * pour un conteneur (bouton, lien…) qui contient au moins une icône et aucun
 * autre texte visible. Le texte d'une icône à ligature (`edit`, `close`…) ne
 * compte pas : c'est un glyphe, pas un libellé.
 */
export function isIconOnly(element: Element): boolean {
  if (element.matches(TOOLTIP_ICON_SELECTOR)) return true;
  if (!element.querySelector(TOOLTIP_ICON_SELECTOR)) return false;
  return textOutsideIcons(element) === '';
}

function textOutsideIcons(element: Element): string {
  const walker = element.ownerDocument.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT
  );
  let text = '';
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const icon = node.parentElement?.closest(TOOLTIP_ICON_SELECTOR);
    if (!icon || !element.contains(icon)) text += node.textContent ?? '';
  }
  return text.trim();
}

/**
 * Retire le `title` natif et le garde en mémoire, pour que le navigateur
 * n'affiche jamais sa propre bulle (retardée) en doublon de la nôtre.
 *
 * Sur une icône seule, le `title` faisait aussi office de nom accessible : il
 * est alors reporté en `aria-label` (sauf si l'élément est déjà nommé), et
 * tenu à jour aux neutralisations suivantes.
 */
export function neutralizeNativeTitle(element: Element): void {
  const title = element.getAttribute(TITLE);
  if (title === null) return;
  element.removeAttribute(TITLE);
  const text = title.trim();
  if (!text) {
    element.removeAttribute(TOOLTIP_NEUTRALIZED_TITLE_ATTRIBUTE);
    return;
  }
  element.setAttribute(TOOLTIP_NEUTRALIZED_TITLE_ATTRIBUTE, text);

  const labelIsOurs = element.hasAttribute(TOOLTIP_DERIVED_LABEL_ATTRIBUTE);
  const alreadyNamed =
    element.hasAttribute(ARIA_LABELLEDBY) ||
    (element.hasAttribute(ARIA_LABEL) && !labelIsOurs);
  if (!alreadyNamed && isIconOnly(element)) {
    element.setAttribute(ARIA_LABEL, text);
    element.setAttribute(TOOLTIP_DERIVED_LABEL_ATTRIBUTE, '');
  }
}

/**
 * Texte d'infobulle porté par l'élément, par priorité : date avec heure,
 * texte explicite (`appTooltip`), ancien `title`, puis `aria-label` d'une
 * icône seule. `null` si l'élément ne déclenche rien.
 *
 * Lecture pure : appeler {@link neutralizeNativeTitle} avant.
 */
export function readTooltipText(
  element: Element,
  formatDateTime: DateTimeTooltipFormatter
): string | null {
  const rawDateTime = element.getAttribute(TOOLTIP_DATETIME_ATTRIBUTE);
  const dateTime = rawDateTime ? formatDateTime(rawDateTime) : null;
  const ariaLabel = isIconOnly(element) ? element.getAttribute(ARIA_LABEL) : '';
  const candidates = [
    dateTime,
    element.getAttribute(TOOLTIP_TEXT_ATTRIBUTE),
    element.getAttribute(TOOLTIP_NEUTRALIZED_TITLE_ATTRIBUTE),
    ariaLabel,
  ];
  return candidates.map((text) => text?.trim()).find((text) => !!text) ?? null;
}

/**
 * Remonte depuis la cible d'un événement jusqu'au premier élément qui porte
 * une infobulle. Les ancêtres candidats sans texte (un champ nommé par
 * `aria-label`, une date sans heure…) sont franchis.
 */
export function findTooltipTrigger(
  target: Element,
  formatDateTime: DateTimeTooltipFormatter
): TooltipTrigger | null {
  let candidate = target.closest(TOOLTIP_CANDIDATE_SELECTOR);
  while (candidate) {
    neutralizeNativeTitle(candidate);
    const text = readTooltipText(candidate, formatDateTime);
    if (text) return { anchor: candidate, text };
    candidate =
      candidate.parentElement?.closest(TOOLTIP_CANDIDATE_SELECTOR) ?? null;
  }
  return null;
}
